import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { useNetwork } from './NetworkContext';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:8000';

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { isOnline } = useNetwork();

  useEffect(() => {
    // Only connect if authenticated and online
    if (!isAuthenticated || !isOnline) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection
    const newSocket = io(WS_URL, {
      auth: {
        userId: user?.id,
        token: localStorage.getItem('token'),
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ['websocket', 'polling'], // Fallback to polling if WebSocket blocked
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      toast.success('Connected to live updates', {
        duration: 2000,
        icon: '🔄',
      });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Reconnection attempt ${attemptNumber}`);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`Reconnected after ${attemptNumber} attempts`);
      toast.success('Reconnected to live updates', {
        duration: 2000,
        icon: '✅',
      });
    });

    newSocket.on('reconnect_failed', () => {
      toast.error('Unable to connect to server. Using offline mode.', {
        duration: 5000,
      });
    });

    // Real-time event handlers
    newSocket.on('patient:updated', (data) => {
      console.log('Patient updated:', data);
      toast('Patient data updated', {
        duration: 2000,
        icon: '👤',
      });
    });

    newSocket.on('alert:new', (data) => {
      console.log('New alert:', data);
      toast.error(`New Alert: ${data.title}`, {
        duration: 5000,
        icon: '🚨',
      });
    });

    newSocket.on('alert:acknowledged', (data) => {
      console.log('Alert acknowledged:', data);
      toast.success('Alert acknowledged', {
        duration: 2000,
      });
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [isAuthenticated, isOnline, user]);

  return (
    <WebSocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
